import type * as Shopify from "../shopify/shopify.server.ts"

import * as UI from "@iyk/ui"
import * as ReactRadioGroup from "@radix-ui/react-radio-group"
import * as React from "react"

import invariant from "tiny-invariant"

import { each } from "@iyk/flow"
import { toSentenceCase } from "@iyk/string"
import { Link, useSearchParams, type LinkProps } from "@remix-run/react"
import { ItemMedia } from "../../components/profiles/item-media.tsx"
import { ProductPrice } from "../../components/shop/product-price.tsx"
import { useIntersection } from "../hooks/use-intersection.ts"

// #region Grid

export const Grid = ({ className, ...props }: React.ComponentProps<"div">) => (
  <div className={classNameForGrid({ className })} {...props} />
)
const classNameForGrid = UI.cva([
  "grid",
  "grid-cols-1",
  "sm:grid-cols-2",
  "md:grid-cols-3",
  "gap-10",
])

// #endregion

// #region Item

export const Item = ({
  className,
  to,
  mediaUrl,
  creator,
  name,
  children,
  shopifyProductData,
  ...props
}: ItemProps) => (
  <Link to={to} className={classNameForItem({ className })} {...props}>
    <ItemMedia src={mediaUrl} className="size-full" />
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1 md:flex-row md:items-baseline md:justify-between">
        <div className="flex flex-col gap-1">
          <span className="text-xs-upper truncate">{creator}</span>
          <span className="text-sm truncate">{name}</span>
        </div>
        {shopifyProductData && (
          <ProductPrice
            size="sm"
            price={shopifyProductData.priceRange.minVariantPrice}
            compareAtPrice={shopifyProductData.compareAtPriceRange.minVariantPrice}
            availableForSale={shopifyProductData.availableForSale}
          />
        )}
      </div>
      {children}
    </div>
  </Link>
)

type ItemProps = LinkProps & {
  to: string
  mediaUrl: string
  creator: string
  name: string
  shopifyProductData?: Shopify.ProductPreview
}

const classNameForItem = UI.cva(["flex", "flex-col", "gap-4"])

// #endregion

// #region Header

export const Header = ({ className, ...props }: React.ComponentProps<"div">) => (
  <div className={classNameForListHeader({ className })} {...props} />
)
const classNameForListHeader = UI.cva(["py-4", "flex", "items-center", "justify-between"])

// #endregion

// #region ItemsCount

export const ItemsCount = ({ count }: { count: number }) => (
  <p className="text-gray-11 text-xs">Items • {count}</p>
)

// #endregion

// #region EmptyState

export const EmptyState = ({ className, children, heading, ...props }: EmptyStateProps) => (
  <div className={classNameForEmptyState({ className })} {...props}>
    <p className="text-center text-sm">{heading}</p>
    {children}
  </div>
)

export type EmptyStateProps = React.ComponentProps<"div"> & {
  heading: string
}

const classNameForEmptyState = UI.cva([
  "flex",
  "flex-col",
  "gap-2",
  "items-center",
  "justify-center",
  // We can't use a fixed height because we also have a margin-top from footer
  "pt-48 pb-24",
])

// #endregion

// #region ItemsOrder

export function ItemsOrder<const T extends readonly Order[]>({
  of,
  defaultValue,
  label,
  queryParam = "order",
  ...props
}: ItemsOrderProps<T>) {
  const [searchParams, setSearchParams] = useSearchParams()

  const orderSearchParam = searchParams.get(queryParam)
  const orderValue =
    of.find((f) => f.id === orderSearchParam) ?? of.find((f) => f.id === defaultValue)

  invariant(orderValue, "Order value not found")

  const handleOrderChange = React.useCallback(
    (orderId: T[number]["id"]) => {
      setSearchParams(
        (prev) => {
          prev.set(queryParam, orderId)
          return prev
        },
        { preventScrollReset: true },
      )
    },
    [searchParams, setSearchParams, queryParam],
  )

  return (
    <UI.Dropdown.Root {...props}>
      <UI.Dropdown.Trigger className="inline-flex shadow-none items-center">
        <p className="text-gray-11 mr-1 text-xs">{toSentenceCase(label)}:</p>
        <p className="text-xs">{toSentenceCase(orderValue.value)}</p>
        <UI.Icon.ChevronDown />
      </UI.Dropdown.Trigger>
      <UI.Dropdown.Content
        align="end"
        className="min-w-44"
        onCloseAutoFocus={(e) => e.preventDefault()} // prevent focus on trigger when closing
      >
        <UI.Dropdown.RadioGroup
          indicatorSide="left"
          value={orderValue.id}
          onValueChange={handleOrderChange}
        >
          {each(of, (order) => (
            <UI.Dropdown.RadioItem key={order.id} value={order.id}>
              {toSentenceCase(order.value)}
            </UI.Dropdown.RadioItem>
          ))}
        </UI.Dropdown.RadioGroup>
      </UI.Dropdown.Content>
    </UI.Dropdown.Root>
  )
}

type Order = {
  id: string
  value: string
}

export type ItemsOrderProps<T extends readonly Order[]> = React.ComponentProps<
  typeof UI.Dropdown.Root
> & {
  of: T
  defaultValue: T[number]["id"]
  label: string
  queryParam?: string
}

// #endregion

// #region ItemsFilter

export function ItemsFilter<const T extends readonly FilterOption[]>({
  of,
  defaultValue,
  queryParam = "filter",
  ...props
}: ItemsFilterProps<T>) {
  const [searchParams, setSearchParams] = useSearchParams()

  const filterSearchParam = searchParams.get(queryParam)
  const filterValue =
    of.find((f) => f.id === filterSearchParam) ?? of.find((f) => f.id === defaultValue)

  invariant(filterValue, "Filter value not found")

  const handleFilterChange = React.useCallback(
    (filterId: T[number]["id"]) => {
      setSearchParams(
        (prev) => {
          prev.set(queryParam, filterId)
          return prev
        },
        { preventScrollReset: true },
      )
    },
    [searchParams, setSearchParams, queryParam],
  )

  return (
    <ReactRadioGroup.Root
      name="variant"
      defaultValue={defaultValue}
      value={filterValue.id}
      onValueChange={handleFilterChange}
      className="flex gap-2"
      {...props}
    >
      {each(of, (filter) => {
        return (
          <div key={filter.id}>
            <ReactRadioGroup.Item value={filter.id} id={filter.id} className="peer sr-only" />
            <label htmlFor={filter.id} className={classForRadioGroupItem()}>
              <span>{toSentenceCase(filter.value)}</span>
              <span>{filter.quantity}</span>
            </label>
          </div>
        )
      })}
    </ReactRadioGroup.Root>
  )
}

const classForRadioGroupItem = UI.cva([
  // Base
  "px-2 py-1 outline-none cursor-pointer select-none",
  "bshadow rounded text-xs",
  "flex items-center justify-center gap-2",
  // Checked
  "peer-data-[state=checked]:bg-gray-12",
  "peer-data-[state=checked]:text-gray-1",
  "peer-data-[state=checked]:bshadow-gray-12",
  // Unchecked
  "peer-data-[state=unchecked]:hover:bg-gray-2",
  "peer-data-[state=unchecked]:bshadow-gray-6",
  // Quantity - common
  "[&>span:last-child]:size-5",
  "[&>span:last-child]:rounded",
  "[&>span:last-child]:text-center",
  "[&>span:last-child]:content-center",
  // Quantity - checked
  "[&>span:last-child]:peer-data-[state=checked]:dark:bg-gray-11",
  "[&>span:last-child]:peer-data-[state=checked]:dark:text-gray-3-light",
  "[&>span:last-child]:peer-data-[state=checked]:bg-gray-11",
  "[&>span:last-child]:peer-data-[state=checked]:text-gray-1",
  // Quantity - unchecked
  "[&>span:last-child]:peer-data-[state=unchecked]:bg-gray-3",
  "[&>span:last-child]:peer-data-[state=unchecked]:text-gray-11",
])

type FilterOption = {
  id: string
  value: string
  quantity: number
}

export type ItemsFilterProps<T extends readonly FilterOption[]> = React.ComponentProps<
  typeof ReactRadioGroup.Root
> & {
  of: T
  defaultValue: T[number]["id"]
  queryParam?: string
}

// #endregion

// #region InfiniteScroll

export const InfiniteScroll = ({ loadNext }: InfiniteScrollProps) => {
  const ref = useIntersection<HTMLDivElement>({
    onIntersect(entry) {
      if (entry.isIntersecting) {
        loadNext()
      }
    },
    options: { threshold: 0.15 },
  })
  return <div ref={ref} />
}

type InfiniteScrollProps = {
  loadNext: () => void | Promise<void>
}

// #endregion
